import type { FC } from 'react';
import { capitalCase } from 'change-case';

import type { InboxItem } from '@/services/database';
import type { CommentMentionTarget, Member, ResourceActivity, ResourceActivityResourceType, User } from '@/types';
import { ResourceActivityEventType } from '@/types';

import CommentMentionTag from '../CommentMentionTag';

interface ResourceActivityActorIdentityProps {
    activity: ResourceActivity;
    popupZIndex?: number;
}

interface ResourceActivityResourceIdentityProps {
    item: InboxItem;
    popupZIndex?: number;
}

interface ActivityActorIdentityProps {
    interactive?: boolean;
    member?: Member;
    popupZIndex?: number;
    user?: User;
}

interface ActivityResourceIdentityProps {
    href?: string;
    id: string;
    interactive?: boolean;
    label: string;
    listType?: string;
    popupZIndex?: number;
    resourceType: ResourceActivityResourceType;
    updatedAt?: string;
    website?: string;
}

const metadataString = (metadata: Record<string, unknown> | undefined, keys: string[]) => {
    for (const key of keys) {
        const value = metadata?.[key];

        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return undefined;
};

const actorMention = (user?: User, member?: Member): CommentMentionTarget | undefined => {
    const actor = user ?? member?.user;

    if (!actor) {
        return undefined;
    }

    const label = `${actor.firstName ?? ''} ${actor.lastName ?? ''}`.trim() || actor.emailAddress || 'Workspace member';
    const id = member?.id ?? actor.id;

    return {
        avatar: actor.avatar,
        handle: actor.emailAddress,
        id,
        key: `member:${id}`,
        kind: 'member',
        label,
        userId: actor.id,
    };
};

const resourceMention = (item: InboxItem): CommentMentionTarget => {
    const metadata = item.activity.metadata ?? {};
    const isComment = item.activity.eventType === ResourceActivityEventType.COMMENTED;
    const label =
        metadataString(
            metadata,
            isComment
                ? [
                      'resourceName',
                      'name',
                      'dashboardName',
                      'folderName',
                      'investmentName',
                      'listName',
                      'memoName',
                      'pipelineName',
                      'reportName',
                      'taskName',
                      'teamName',
                  ]
                : ['targetName', 'resourceName', 'name'],
        ) ||
        (isComment ? capitalCase(item.resourceType.replace(/s$/, '')) : item.activity.title || 'Workspace resource');
    const website = metadataString(metadata, ['website', 'companyWebsite']);
    const listType = item.resourceType === 'lists' ? metadataString(metadata, ['listType', 'type']) : undefined;

    return {
        href: item.url,
        id: item.resourceId,
        key: `resource:${item.resourceType}:${item.resourceId}`,
        kind: 'resource',
        label,
        listType,
        resourceType: item.resourceType,
        updatedAt: new Date(item.activity.updatedAt ?? item.activity.createdAt).toISOString(),
        website,
    };
};

export const ResourceActivityActorIdentity: FC<ResourceActivityActorIdentityProps> = ({ activity, popupZIndex }) => {
    return <ActivityActorIdentity member={activity.actorMember} popupZIndex={popupZIndex} user={activity.actor} />;
};

export const ActivityActorIdentity: FC<ActivityActorIdentityProps> = ({
    interactive = false,
    member,
    popupZIndex,
    user,
}) => {
    const mention = actorMention(user, member);

    return mention ? (
        <CommentMentionTag interactive={interactive} mention={mention} popupZIndex={popupZIndex} />
    ) : (
        <span>System</span>
    );
};

export const ActivityResourceIdentity: FC<ActivityResourceIdentityProps> = ({
    href,
    id,
    interactive = Boolean(href),
    label,
    listType,
    popupZIndex,
    resourceType,
    updatedAt,
    website,
}) => (
    <CommentMentionTag
        interactive={interactive}
        mention={{
            href,
            id,
            key: `resource:${resourceType}:${id}`,
            kind: 'resource',
            label,
            listType,
            resourceType,
            updatedAt,
            website,
        }}
        popupZIndex={popupZIndex}
    />
);

export const ResourceActivityResourceIdentity: FC<ResourceActivityResourceIdentityProps> = ({ item, popupZIndex }) => (
    <CommentMentionTag interactive={false} mention={resourceMention(item)} popupZIndex={popupZIndex} />
);
